import deepEqual from "deep-equal";

import type { Database, DocumentData, UserSpecificRow } from "../common/Database";
import type { TableGlideType } from "../common/description";
import { assert, isUndefinedish, truthify } from "../support";
import type { Value, ValueProducer } from "../common/components/types";
import { Data } from "../common/components/types";
import { isRefObject, isKeyRefObject, Data as DataData, ArrayRefValueProducer } from "../common/components/data";
import { isLoadedDefined } from "../common/components/component-helpers";
import { convertGlideDateTime } from "../common/glide-date-time";

function liftValueIntoProducer(db: Database, value: unknown, parent: ValueProducer): ValueProducer {
    const v = db.convertDocumentProperty(value);
    assert(!isUndefinedish(v));

    if (Array.isArray(v)) {
        return new Data(v.map(i => new DataData(i)));
    } else if (isRefObject(v) && !isKeyRefObject(v)) {
        return new ArrayRefValueProducer(v.$arrayItems, parent);
    } else {
        return new DataData(v as Value);
    }
}

function liftValueForUpdate(db: Database, value: unknown, parent: ValueProducer): ValueProducer | Value {
    const v = db.convertDocumentProperty(value);
    assert(!isUndefinedish(v));

    if (isRefObject(v) && !isKeyRefObject(v)) {
        return new ArrayRefValueProducer(v.$arrayItems, parent);
    } else {
        return v as Value;
    }
}

export function updateRowData(
    db: Database,
    rowData: ValueProducer,
    updates: DocumentData,
    table: TableGlideType,
    onlyUserSpecific: boolean
): void {
    const inspector = rowData.getObjectInspector(undefined);
    if (!isLoadedDefined(inspector)) return;

    let isDirty = false;

    function addPropertyAvoidingUpdates(name: string, vp: ValueProducer) {
        // Generally speaking, we don't want to reset value producers
        // for row-specific column data if we can help it. When we can,
        // we want to use columnData.set(). We can't do that for
        // ArrayRefValueProducers though, instead we have to remove the
        // property and re-set it on the row itself, no matter what.
        if (vp instanceof ArrayRefValueProducer) {
            if (onlyUserSpecific) return;

            if (rowData instanceof Data) {
                rowData.removePropertyWithoutUpdate(name);
                rowData.addPropertyWithoutUpdate(name, vp);
                isDirty = true;
            } else {
                rowData.removeProperty(name);
                rowData.addProperty(name, vp);
            }
        } else if (rowData instanceof Data) {
            rowData.addPropertyWithoutUpdate(name, vp);
            isDirty = true;
        } else {
            rowData.addProperty(name, vp);
        }
    }

    function removePropertyAvoidingUpdates(name: string) {
        if (rowData instanceof Data) {
            rowData.removePropertyWithoutUpdate(name);
            isDirty = true;
        } else {
            rowData.removeProperty(name);
        }
    }

    // ##updateRowData:
    // This applies `updates` to the row data, where
    // `updates` comes from Firestore, so we have to convert to
    // `GlideDateTime` if necessary.
    for (const { name, isUserSpecific, formula } of table.columns) {
        if (onlyUserSpecific && !truthify(isUserSpecific)) continue;
        if (formula !== undefined) continue;

        const value = convertGlideDateTime(updates[name]);
        const columnData = inspector.getProperty(name);
        if (columnData === undefined) {
            if (value === undefined) continue;
            addPropertyAvoidingUpdates(name, liftValueIntoProducer(db, value, rowData));
        } else if (value === undefined) {
            if (name in updates) {
                // `name in updates` ensures that we only remove properties
                // when the name is explicitly set to 'undefined' inside the
                // updates object; this signals a deletion vs a do-nothing
                // case.
                removePropertyAvoidingUpdates(name);
            }
        } else {
            const lifted = liftValueForUpdate(db, value, rowData);
            if (lifted instanceof ArrayRefValueProducer) {
                addPropertyAvoidingUpdates(name, lifted);
            } else {
                columnData.set(value);
            }
        }
    }

    if (isDirty && rowData instanceof Data) {
        rowData.setListenersDirty();
    }
}

export function compareUserSpecificRowsForApplication(left: UserSpecificRow, right: UserSpecificRow) {
    if (left.$rowVersion === undefined) {
        if (right.$rowVersion === undefined) return 0;
        return -1;
    } else if (right.$rowVersion === undefined) {
        return 1;
    } else {
        return left.$rowVersion - right.$rowVersion;
    }
}

export function liftUserSpecificData(
    table: TableGlideType,
    reverseSortedUserDatas: readonly UserSpecificRow[] | undefined
): DocumentData {
    const ret: DocumentData = {};
    if (reverseSortedUserDatas === undefined) return ret;

    for (const { name, isUserSpecific, formula } of table.columns) {
        // FIXME: use `isComputedColumn` instead of looking at `formula`
        if (!truthify(isUserSpecific) || formula !== undefined) continue;

        for (const userData of reverseSortedUserDatas) {
            const value = convertGlideDateTime(userData.data[name]);
            if (value !== undefined) {
                ret[name] = userData.data[name];
                break;
            }
        }
    }
    return ret;
}

/**
 * Reports the difference between right and left, i.e. right - left.
 *
 * Each key-value pair for which the key is missing in left but present
 * in right is returned as part of the returned object.
 *
 * For each key in left:
 * 1. If right does not contain the same key, the value for the key
 *    in the returned object is explicitly "undefined" to signify
 *    that the value has been deleted.
 * 2. If right contains the same key but a different value for the key,
 *    the value for the key in the returned object is the value for the
 *    key in the right object.
 * 3. Otherwise, left and right contain the same value for the same key,
 *    and the key is removed entirely from the returned object.
 *
 * There are two ways in which this behavior can be modified:
 * 1. If a key is present in the alwaysInclude set, the value for the key
 *    in the right object will be set as the value for the key in the
 *    returned object, so long as said key also exists in the left object.
 * 2. If a key is present in the neverInclude set, even if the key were
 *    expected to be set by the usual process, it is not present in the
 *    returned object.
 */
export function diffDocumentData(
    left: DocumentData,
    right: DocumentData,
    alwaysInclude: ReadonlySet<string> | undefined,
    neverInclude: ReadonlySet<string> | undefined
): DocumentData {
    const ret = { ...right };
    const keysSet = new Set([...Object.keys(left), ...Object.keys(right)]);
    for (const key of keysSet) {
        if (alwaysInclude?.has(key) === true) {
            // This is right - left, hence we always return the value
            // of the right key.
            ret[key] = right[key];
            continue;
        }
        if (neverInclude?.has(key) === true) {
            delete ret[key];
            continue;
        }

        if (left[key] === undefined) {
            continue;
        } else if (right[key] === undefined) {
            // Explicitly set "undefined" is equivalent to "deleted".
            // We can still test for this with (key in ret).
            ret[key] = undefined;
        } else if (deepEqual(left[key], right[key], { strict: true })) {
            delete ret[key];
        }
    }
    return ret;
}

export function getOnlyColumns(doc: DocumentData, columnNames: Iterable<string>): DocumentData {
    const result: DocumentData = {};
    for (const n of columnNames) {
        result[n] = doc[n];
    }
    return result;
}
